/**
 * **규격에 맞게 줄인다** — 올린 그림이 슬롯 상한보다 크면 여기서 맞춘다.
 *
 * ## 왜 필요한가
 * AI(Google AI Studio·ChatGPT)는 **1024~2048px** 로 뽑는다. 그런데 화면에 그려지는 크기는
 * 소품 48px · 아이콘 32~48px 이라 슬롯 상한이 512px 다. 그대로 올리면
 * `✗ 해상도 초과 1024px > 512px` 로 **막히고 끝난다.**
 *
 * 상한을 푸는 건 답이 아니다 — 40배 큰 그림을 받으면 **아이 폰에서 매번 줄이는 비용**이 된다.
 * 그래서 상한은 두고, **여기서 줄여 준다.**
 *
 * ## 원본은 버리지 않는다
 * 이건 **화면용** 그림을 만드는 것이다. 굿즈 인쇄에 쓸 큰 원본은 `assets-src/` 에 따로 남긴다
 * (`pnpm art:src`). 원본을 버려서 지금 123개가 인쇄 불가 상태다 — 같은 실수를 반복하지 않는다.
 */

/** 줄인 뒤 WebP 로 낸다 — 모든 그림 슬롯이 webp 를 받고, 같은 화질에 파일이 훨씬 작다. */
const OUT_TYPE = "image/webp";
const OUT_QUALITY = 0.92;

export interface ResizeResult {
  file: File;
  dataUrl: string;
  from: { width: number; height: number };
  to: { width: number; height: number };
}

function loadImage(src: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    if (typeof Image === "undefined") return resolve(null);
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

/**
 * 긴 변이 `maxLongEdge` 가 되도록 비율을 지켜 줄인다.
 * 이미 작으면 `null` — **키우지 않는다**(없는 화질이 생기지 않는다).
 */
export async function resizeFile(
  file: File,
  dataUrl: string,
  maxLongEdge: number,
): Promise<ResizeResult | null> {
  const img = await loadImage(dataUrl);
  if (!img || !img.naturalWidth || typeof document === "undefined") return null;

  const from = { width: img.naturalWidth, height: img.naturalHeight };
  const long = Math.max(from.width, from.height);
  if (long <= maxLongEdge) return null;

  const s = maxLongEdge / long;
  const to = {
    width: Math.max(1, Math.round(from.width * s)),
    height: Math.max(1, Math.round(from.height * s)),
  };

  const c = document.createElement("canvas");
  c.width = to.width;
  c.height = to.height;
  const ctx = c.getContext("2d");
  if (!ctx) return null;
  // 기본값으로 줄이면 계단이 진다 — 작은 그림일수록 티가 난다
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, 0, 0, to.width, to.height);

  const blob = await new Promise<Blob | null>((res) => c.toBlob(res, OUT_TYPE, OUT_QUALITY));
  if (!blob) return null;

  const name = file.name.replace(/\.[^.]+$/, "") + ".webp";
  return {
    file: new File([blob], name, { type: OUT_TYPE }),
    dataUrl: c.toDataURL(OUT_TYPE, OUT_QUALITY),
    from,
    to,
  };
}
